import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { Subject, takeUntil } from 'rxjs';
import {
  CRM_PIPELINE_COLUMNS,
  CRM_PRIORITY_COLORS,
  CRM_PRIORITY_LABELS,
  CrmLead,
  CrmPipelineColumn,
} from '../../../models/crm-lead.model';
import { CrmLeadService } from '../../../services/crm-lead.service';

@Component({
  selector: 'app-admin-crm-pipelines',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './admin-crm-pipelines.component.html',
  styleUrls: ['./admin-crm-pipelines.component.scss'],
})
export class AdminCrmPipelinesComponent implements OnInit, OnDestroy {
  private readonly crm = inject(CrmLeadService);
  private readonly router = inject(Router);
  private readonly destroy$ = new Subject<void>();

  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly leads = signal<CrmLead[]>([]);

  readonly CRM_PRIORITY_LABELS = CRM_PRIORITY_LABELS;
  readonly CRM_PRIORITY_COLORS = CRM_PRIORITY_COLORS;

  readonly board = computed(() =>
    CRM_PIPELINE_COLUMNS.map(col => ({
      ...col,
      leads: this.leadsForColumn(col),
      totalEstimate: this.columnEstimate(col),
    })),
  );

  ngOnInit(): void {
    this.crm.watchLeads()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: leads => {
          this.leads.set(leads.filter(l => l.status !== 'lost' && l.status !== 'nurturing'));
          this.loading.set(false);
          this.loadError.set(null);
        },
        error: err => {
          this.loading.set(false);
          this.loadError.set(err?.message ?? 'No se pudo cargar el pipeline.');
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openLead(id: string): void {
    void this.router.navigate(['/admin/crm/leads', id]);
  }

  formatEstimate(eur: number): string {
    if (!eur) return '—';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(eur);
  }

  private leadsForColumn(col: CrmPipelineColumn): CrmLead[] {
    return this.leads().filter(l => col.statuses.includes(l.status));
  }

  private columnEstimate(col: CrmPipelineColumn): number {
    return this.leadsForColumn(col).reduce(
      (sum, l) => sum + (l.estimatedHourlyEur ?? 0),
      0,
    );
  }
}
